import { type FC, type RefObject } from 'react';

import { type CustomCellRendererProps } from 'ag-grid-react';
import {
  IconDownload,
  IconEye,
  IconLoader2,
} from '@tabler/icons-react';
import { ButtonField } from '@wk/components-react16';

import type { Evaluation } from '@/types';
import { cn } from '@/lib/utils';
import { renderSource } from '@/modules/utils/cell-renderers';

import {
  formatEvaluationGeneratedAt,
  getScoreIndicatorColorClass,
} from './ApplicationDetailsPage.utils';

interface ApplicationDetailsMobileListProps {
  activeEvaluationId: string | null;
  evaluationRows: Evaluation[];
  isExportingCsv: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  loadMoreSentinelRef: RefObject<HTMLDivElement | null>;
  onDownloadEvaluation: (evaluation: Evaluation) => void;
  onNavigateToEvaluation: (evaluationId: string) => void;
}

const ApplicationDetailsMobileList: FC<ApplicationDetailsMobileListProps> = ({
  activeEvaluationId,
  evaluationRows,
  isExportingCsv,
  isFetchingNextPage,
  isLoading,
  loadMoreSentinelRef,
  onDownloadEvaluation,
  onNavigateToEvaluation,
}) => {
  if (isLoading) {
    return (
      <div className="flex h-full min-h-[200px] items-center justify-center text-gray-500">
        <IconLoader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (evaluationRows.length === 0) {
    return (
      <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
        No evaluations found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {evaluationRows.map((evaluation) => (
        <article
          key={evaluation.id}
          className="rounded-lg border bg-white p-3 shadow-sm"
          role="button"
          tabIndex={0}
          onClick={() => onNavigateToEvaluation(evaluation.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onNavigateToEvaluation(evaluation.id);
            }
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {formatEvaluationGeneratedAt(evaluation.generatedAt)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ButtonField mode="ghost" size="small">
                <button
                  type="button"
                  aria-label="View evaluation"
                  onClick={(event) => {
                    event.stopPropagation();
                    onNavigateToEvaluation(evaluation.id);
                  }}
                >
                  <IconEye size={14} />
                </button>
              </ButtonField>
              <ButtonField mode="ghost" size="small">
                <button
                  type="button"
                  aria-label="Download report"
                  disabled={
                    isExportingCsv && activeEvaluationId === evaluation.id
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onDownloadEvaluation(evaluation);
                  }}
                >
                  {isExportingCsv && activeEvaluationId === evaluation.id ? (
                    <IconLoader2 className="animate-spin" size={14} />
                  ) : (
                    <IconDownload size={14} />
                  )}
                </button>
              </ButtonField>
            </div>
          </div>

          <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-x-3 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <dt className="whitespace-nowrap text-sm uppercase tracking-wide text-gray-500">
                Score
              </dt>
              <dd className="flex items-center gap-2">
                <span
                  className={cn(
                    'h-2 w-2 rounded-full',
                    getScoreIndicatorColorClass(evaluation.score),
                  )}
                />
                <span>
                  {evaluation.score === null || evaluation.score === undefined
                    ? 'N/A'
                    : `${evaluation.score}%`}
                </span>
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="whitespace-nowrap text-sm uppercase tracking-wide text-gray-500">
                Checkpoints
              </dt>
              <dd>{evaluation.totalCheckpoints}</dd>
            </div>
            <div className="w-fit justify-self-end">
              <dt className="sr-only">Source</dt>
              <dd className="flex items-end" aria-label="Source">
                {renderSource({
                  data: evaluation,
                } as CustomCellRendererProps<Evaluation>)}
              </dd>
            </div>
          </dl>
        </article>
      ))}
      {isFetchingNextPage ? (
        <div className="flex items-center justify-center py-2 text-gray-500">
          <IconLoader2 size={18} className="animate-spin" />
        </div>
      ) : null}
      <div
        aria-hidden="true"
        ref={loadMoreSentinelRef}
        className="h-px w-full"
      />
    </div>
  );
};

export default ApplicationDetailsMobileList;
